import { useEffect, useState } from 'react'
import { ArrowUpRight, Inbox } from 'lucide-react'
import type { CfoSharedReport, ServerApiClient } from '../../../../data/ServerApiClient'
import { buildCfoRoutePath, parseCfoRouteState } from '../cfoRoutes'

// "Shared with me" inbox — surfaces the CFO reports other preparers have shared
// with the signed-in user (via cfo_report_shares user/group grants). Clicking a
// card opens that workbook's Presentations page through the normal
// authenticated view path (report_share_grants_view covers recipients without
// project access). Self-hides when the user has no shares, so the studio stays
// calm for preparers who are only sharing outward.

type CfoSharedReportsInboxProps = {
  appApi: ServerApiClient
  /** The workbook already on screen — excluded from the list. */
  currentWorkbookId?: string | null
}

export function CfoSharedReportsInbox({ appApi, currentWorkbookId = null }: CfoSharedReportsInboxProps) {
  const [reports, setReports] = useState<CfoSharedReport[]>([])
  const [loaded, setLoaded] = useState(false)

  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const res = await appApi.listCfoSharedReports()
        if (!cancelled) setReports(Array.isArray(res) ? res : [])
      } catch {
        // Non-fatal: an empty inbox renders nothing.
        if (!cancelled) setReports([])
      } finally {
        if (!cancelled) setLoaded(true)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [appApi])

  const visible = reports.filter((report) => report.workbook_id !== (currentWorkbookId || ''))
  if (!loaded || visible.length === 0) return null

  const openShared = (report: CfoSharedReport) => {
    // Internal shares live within the recipient's site; reuse the slug from the
    // current location (the only place the slug exists client-side).
    const route = typeof window !== 'undefined' ? parseCfoRouteState(window.location) : null
    const siteSlug = route?.siteSlug || ''
    if (!siteSlug) return
    window.location.href = buildCfoRoutePath({
      siteSlug,
      workbookId: report.workbook_id,
      page: 'presentations',
    })
  }

  return (
    <section className="cfo-shared-inbox rise" data-testid="cfo-shared-reports-inbox">
      <div className="cfo-shared-inbox-head">
        <Inbox size={15} />
        <h2 className="ff-guide">Shared with me</h2>
        <span className="meta">{visible.length} report{visible.length === 1 ? '' : 's'}</span>
      </div>
      <div className="cfo-shared-inbox-grid">
        {visible.map((report) => (
          <button
            key={report.workbook_id}
            type="button"
            className="cfo-shared-inbox-card"
            onClick={() => openShared(report)}
            data-testid={`cfo-shared-report-${report.workbook_id}`}
          >
            <div className="cfo-shared-inbox-meta">
              <strong>{report.workbook_name || 'Shared report'}</strong>
              <span>{report.audience_label || 'View-only board pack'}</span>
            </div>
            <ArrowUpRight size={15} />
          </button>
        ))}
      </div>
    </section>
  )
}

export default CfoSharedReportsInbox
